import React, { useEffect, useState } from 'react';
import {
  Save, CreditCard, Undo2, Timer, UtensilsCrossed, BadgeCheck, Zap, UserCheck, UserX, Globe, LayoutGrid, CalendarX, ShieldCheck, Info, X, Wallet, Hourglass, Users, MapPin,
} from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { useRestaurant } from '../context/RestaurantContext';
import apiClient from '../services/api';
import { confirm } from '../components/ui/confirm-dialog';

const DEFAULTS = {
  online_reservations_enabled: true,
  payment_required: false,
  booking_amount: 0,
  amount_per: 'BOOKING',
  refund_allowed: true,
  refund_cutoff_minutes: 15,
  refund_method: 'SOURCE',
  cancellation_allowed: true,
  cancel_cutoff_minutes: 60,
  hold_table_minutes: 15,
  allow_preorder: false,
  auto_confirm: false,
  online_bookable_table_ids: [],
  // Parallel offline (phone / counter) policy.
  offline_reservations_enabled: true,
  offline_payment_required: false,
  offline_booking_amount: 0,
  offline_amount_per: 'BOOKING',
  offline_refund_allowed: true,
  offline_refund_cutoff_minutes: 15,
  offline_refund_method: 'CASH',
  offline_cancellation_allowed: true,
  offline_cancel_cutoff_minutes: 60,
  offline_hold_table_minutes: 15,
  offline_allow_preorder: false,
  offline_auto_confirm: true,
  offline_bookable_table_ids: [],
  // Phone verification, per kind of staff-taken booking. Not behind the
  // online/offline `keyFor` mapping: both are offline rules, so they are read
  // by name rather than by tab.
  phone_booking_otp_required: false,
  walkin_booking_otp_required: false,
  /* Parallel BUFFET policy — chosen by what the guest is coming FOR rather
     than by which screen the booking arrived on, so it governs a buffet
     booked on the website and one booked at the counter alike.
     These rules always apply to a buffet booking; there is no opt-in switch
     to forget to turn on. Switch the channel itself off below to stop taking
     buffet bookings at all. */
  buffet_reservations_enabled: true,
  buffet_payment_required: false,
  buffet_booking_amount: 0,
  buffet_amount_per: 'GUEST',
  /* FIXED = the deposit you type below; FULL = the whole per-head ticket for
     the party, priced from that day's own buffet prices. Only a buffet can be
     charged in full — it is the one booking whose price is known before
     anybody has arrived or ordered anything. */
  buffet_amount_mode: 'FIXED',
  /* How long the covers an unpaid buffet booking claimed are kept for it.
     0 = no clock at all. */
  buffet_seat_hold_minutes: 5,
  table_hold_minutes: 5,
  /* House rules, not channel rules: the same two numbers answer for a
     website booking, a phone booking and a buffet booking alike. */
  no_show_strikes_limit: 3,
  max_party_size: 0,
  buffet_refund_allowed: true,
  buffet_refund_cutoff_minutes: 15,
  buffet_refund_method: 'SOURCE',
  buffet_cancellation_allowed: true,
  buffet_cancel_cutoff_minutes: 60,
  buffet_hold_table_minutes: 15,
  buffet_allow_preorder: false,
  buffet_auto_confirm: false,
  buffet_bookable_table_ids: [],
};

/* HOW a refunded booking amount gets back to the guest. Policy, not plumbing:
   no gateway sits behind the mock card page, so what this decides is what the
   guest is promised when they book, what they are told the moment they cancel,
   and what the staff screens tell whoever actually pays it out. The public
   reserve page phrases the same four for guests; keep the two wordings in
   step. */
const REFUND_METHODS = [
  { key: 'SOURCE', label: 'Original payment method',
    hint: 'Back to the card or wallet the booking was paid with.' },
  { key: 'CASH', label: 'Cash at the counter',
    hint: 'The guest collects it in person at the restaurant.' },
  { key: 'CREDIT', label: 'House credit',
    hint: 'Held for the guest to spend on a future visit.' },
  { key: 'BANK', label: 'Bank transfer',
    hint: 'Paid to the guest’s account — you collect their details.' },
];

const refundMethodLabel = (key) =>
  (REFUND_METHODS.find((m) => m.key === key) || REFUND_METHODS[0]).label;

function Toggle({ checked, onChange, label, hint, disabled }) {
  return (
    <label className={`flex items-start gap-3 select-none ${disabled ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition ${
          checked ? 'bg-primary' : 'bg-muted-foreground/25'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </button>
      <span>
        <span className="block text-sm font-medium text-foreground">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

/* A number with its unit printed inside the box ("15  min"), sized to the
   number rather than stretched across the card — a four-digit field does not
   need thirty characters of width, and the unit answers "minutes or hours?"
   without lengthening the label. */
function NumberField({ label, value, onChange, unit, hint, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
      <div className="relative w-36">
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 pr-11 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
          type="number"
          min="0"
          value={value}
          placeholder={placeholder}
          onChange={onChange}
        />
        {unit && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-muted-foreground">
            {unit}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>}
    </div>
  );
}

/* Four options, so the two-tile treatment the confirmation card uses would run
   off the edge — a 2×2 grid of the same selectable tiles keeps every choice
   and its consequence readable without a dropdown to open first. */
function MethodPicker({ label, value, onChange, options, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((o) => {
          const on = value === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onChange(o.key)}
              aria-pressed={on}
              className={`text-left rounded-lg border p-3 transition ${
                on
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                  : 'border-border bg-card hover:border-muted-foreground/40'
              }`}
            >
              <span className={`block text-sm font-medium ${on ? 'text-primary' : 'text-foreground'}`}>
                {o.label}
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{o.hint}</span>
            </button>
          );
        })}
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>}
    </div>
  );
}

function Section({ icon: Icon, title, hint, right, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
              <Icon size={16} />
            </span>
            {title}
          </h3>
          {right}
        </div>
        {hint && <p className="text-xs text-muted-foreground mt-1.5 ml-[42px]">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

export default function ReservationSettingsPage() {
  const { t } = useT();
  const { restaurantId, locationId, location } = useRestaurant();
  /* Rules are saved per branch (users/0116) and this page only ever writes the
     branch the user is logged in to. There is no picker here on purpose: the
     branch is switched once, in the account menu at the top right, and every
     screen follows it — a second switcher on this page is how rules get saved
     against a counter nobody was looking at. The server still sends the branch
     list, which is how we know whether this one has rules of its own yet. */
  const [branches, setBranches] = useState([]);
  const branchId = locationId ?? null;
  const branch = branches.find((b) => b.id === branchId) || null;
  const branchName = branch?.name || location?.name || '';
  const locQ = branchId ? `?location=${branchId}` : '';
  const [s, setS] = useState(DEFAULTS);
  // Last-saved snapshot — powers the "unsaved changes" indicator and Discard.
  const [initial, setInitial] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  // The online master switch writes through on its own (see
  // setOnlineReservations) — this holds it steady while that PUT is in flight.
  const [flipping, setFlipping] = useState(false);
  // Floor → section → table tree for the "which tables are bookable" picker.
  const [tablesTree, setTablesTree] = useState([]);
  // Which channel's rules we're editing: 'online' (web booking page) or
  // 'offline' (staff bookings at the counter / over the phone).
  const [tab, setTab] = useState('online');
  /* The ⓘ on the money card. What it lists is deliberately NOT built yet —
     writing the gaps down on the screen they belong to is how staff stop
     hunting for a control that was never there, and how we stop being asked
     for it twice. Closed by default; nothing here affects a saved setting. */
  const [showRoadmap, setShowRoadmap] = useState(false);

  // Map a logical setting name to the actual state key for the active tab.
  const keyFor = (logical) => {
    if (tab === 'buffet') {
      if (logical === 'enabled') return 'buffet_reservations_enabled';
      if (logical === 'bookable') return 'buffet_bookable_table_ids';
      return `buffet_${logical}`;
    }
    if (tab === 'offline') {
      if (logical === 'enabled') return 'offline_reservations_enabled';
      if (logical === 'bookable') return 'offline_bookable_table_ids';
      return `offline_${logical}`;
    }
    if (logical === 'enabled') return 'online_reservations_enabled';
    if (logical === 'bookable') return 'online_bookable_table_ids';
    return logical;
  };
  const val = (logical) => s[keyFor(logical)];
  const setV = (logical, v) => set(keyFor(logical), v);
  const isOnline = tab === 'online';
  const isBuffet = tab === 'buffet';

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    apiClient
      .get(`/users/restaurant/${restaurantId}/reservation-settings/${locQ}`)
      .then(({ data }) => {
        if (data?.success) {
          const loaded = { ...DEFAULTS, ...data.data };
          setS(loaded);
          setInitial(loaded);
        }
        setBranches(Array.isArray(data?.locations) ? data.locations : []);
        setTablesTree(Array.isArray(data?.tables_tree) ? data.tables_tree : []);
      })
      .catch(() => setError('Failed to load reservation settings'))
      .finally(() => setLoading(false));
  }, [restaurantId, branchId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Bookable-tables selection helpers, for the active channel. Empty list =
  // every table is bookable.
  const allTableIds = tablesTree.flatMap((f) => f.sections.flatMap((sec) => sec.tables.map((t) => t.id)));
  const selectedIds = new Set(val('bookable') || []);
  const isTableOn = (id) => selectedIds.size === 0 || selectedIds.has(id);
  const toggleTable = (id) => {
    // First explicit pick converts "all" into a concrete list of everything
    // except the table being switched off — otherwise unchecking one from the
    // implicit "all" state would silently open nothing.
    let next;
    if (selectedIds.size === 0) {
      next = allTableIds.filter((tid) => tid !== id);
    } else if (selectedIds.has(id)) {
      next = allTableIds.filter((tid) => selectedIds.has(tid) && tid !== id);
    } else {
      next = [...selectedIds, id];
    }
    // Selecting everything collapses back to "all" (empty list).
    setV('bookable', next.length === allTableIds.length ? [] : next);
  };
  const toggleSection = (sec) => {
    const ids = sec.tables.map((t) => t.id);
    const allOn = ids.every(isTableOn);
    const base = selectedIds.size === 0 ? new Set(allTableIds) : new Set(selectedIds);
    ids.forEach((id) => (allOn ? base.delete(id) : base.add(id)));
    const next = [...base];
    setV('bookable', next.length === allTableIds.length ? [] : next);
  };

  const set = (f, v) => { setS((p) => ({ ...p, [f]: v })); setSaved(false); };

  const setOnlineReservations = async (v) => {
    const ok = await confirm(
      v
        ? {
            title: 'Open website reservations?',
            description:
              'The public booking page will start accepting new reservations again.',
            confirmText: 'Open reservations',
          }
        : {
            title: 'Close website reservations?',
            description:
              "The public booking page will show 'reservations closed' and no new website bookings will be accepted. Existing bookings are unaffected.",
            confirmText: 'Close reservations',
            tone: 'destructive',
          }
    );
    if (!ok) return;
    if (!branchId) { setError(t('settings.resv.noBranch')); return; }
    /* The confirm above promises the public page changes NOW, so this one
       switch is written straight through instead of waiting for Save — a
       closed sign nobody saved is worse than no switch at all. Only this
       field is sent, so any other unsaved edits on the page stay unsaved.
       A failed write puts the switch back rather than lying about the
       public page. */
    set('online_reservations_enabled', v);
    setError('');
    setFlipping(true);
    try {
      const { data } = await apiClient.put(
        `/users/restaurant/${restaurantId}/reservation-settings/${locQ}`,
        { online_reservations_enabled: v, location: branchId });
      if (!data?.success) throw new Error(data?.error || 'Failed to save');
      setInitial((p) => ({ ...p, online_reservations_enabled: v }));
    } catch (e) {
      setS((p) => ({ ...p, online_reservations_enabled: !v }));
      setError(e?.response?.data?.error || e.message ||
        (v ? 'Could not open website reservations.' : 'Could not close website reservations.'));
    } finally {
      setFlipping(false);
    }
  };

  // Enable/disable the active channel. Online flips the public page (with a
  // confirm); offline just toggles whether staff may take counter bookings.
  const setChannelEnabled = (v) => {
    if (isOnline) return setOnlineReservations(v);
    return setV('enabled', v);
  };

  const save = async () => {
    // Every write is per branch; without one the server refuses the save, so
    // say so here rather than sending a PUT that cannot land anywhere.
    if (!branchId) { setError(t('settings.resv.noBranch')); return; }
    if (s.payment_required && !(Number(s.booking_amount) > 0)) {
      setError('Set a website booking amount greater than zero, or turn website payment off.');
      setTab('online');
      return;
    }
    if (s.offline_payment_required && !(Number(s.offline_booking_amount) > 0)) {
      setError('Set an offline booking amount greater than zero, or turn offline payment off.');
      setTab('offline');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Booking amount is always charged once per booking (both channels).
      const { data } = await apiClient.put(
        `/users/restaurant/${restaurantId}/reservation-settings/${locQ}`,
        { ...s, amount_per: 'BOOKING', offline_amount_per: 'BOOKING', location: branchId });
      if (!data?.success) throw new Error(data?.error || 'Failed to save');
      const saved = { ...DEFAULTS, ...data.data };
      setS(saved);
      setInitial(saved);
      setSaved(true);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify(s) !== JSON.stringify(initial);
  const discard = () => { setS(initial); setSaved(false); setError(''); };

  if (!restaurantId) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          No restaurant selected.
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 pb-6 w-full">
      {/* Title and save controls ride together at the top of the pane. A bar
          docked at the bottom sat across the middle of the form on any screen
          shorter than the page, cutting a section in half; up here it only ever
          covers what has already scrolled past, and Save is in reach without
          hunting for the end of a long form. */}
      <header className="sticky top-0 z-30 -mx-6 mb-6 border-b border-border bg-background/95 px-6 pb-4 pt-6 backdrop-blur">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">
              Reservation Settings
            </h1>
            {/* Which branch these rules are for. Named, not chosen: the page
                writes the branch you are logged in to and no other, so it reads
                as a statement of where you are rather than a control. */}
            {branchId ? (
              <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                <span className="text-muted-foreground">{t('settings.resv.branch')}</span>
                <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2.5 py-1 font-medium text-foreground">
                  <MapPin size={13} className="text-muted-foreground" />
                  {branchName}
                </span>
                <span className="text-xs text-muted-foreground">{t('settings.resv.branchOnly')}</span>
                {branch && !branch.has_settings && (
                  <span className="text-xs text-amber-700">{t('settings.resv.usingFallback')}</span>
                )}
              </div>
            ) : (
              <p className="mt-3 text-sm text-amber-700">{t('settings.resv.noBranch')}</p>
            )}
          </div>
          {!loading && (
            <div className="flex items-center gap-3 flex-wrap">
              {dirty && (
                <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
                </span>
              )}
              <button
                type="button"
                onClick={discard}
                disabled={!dirty || saving}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Discard
              </button>
              <button
                onClick={save}
                disabled={saving || !dirty || !branchId}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
              >
                <Save size={15} /> {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-4 max-w-3xl rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}
      {saved && (
        <div className="mb-4 max-w-3xl rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">
          Reservation settings saved.
        </div>
      )}

      {/* Channel tabs — Online (web) vs Offline (phone / counter). */}
      {!loading && (
        <div className="mb-4 inline-flex rounded-xl border border-border bg-muted/40 p-1">
          {[
            { key: 'online', label: 'Website (online booking)' },
            { key: 'offline', label: 'Offline (phone / counter)' },
            /* The third channel is not a place a booking comes FROM but a
               thing the guest is coming FOR, so it overrides either of the
               two above when the booking is for a buffet. */
            { key: 'buffet', label: 'Buffet bookings' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                tab === t.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading settings…</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
          {/* Left column */}
          <div className="space-y-4">
          {/* Channel master switch */}
          <Section
            icon={Globe}
            title={isBuffet ? 'Buffet reservations' : isOnline ? 'Website reservations' : 'Offline reservations'}
          >
            <Toggle
              checked={val('enabled')}
              onChange={setChannelEnabled}
              disabled={isOnline && flipping}
              label={isBuffet ? 'Accept buffet reservations'
                : isOnline ? 'Accept website reservations' : 'Accept offline (counter) reservations'}
              hint={isBuffet
                ? 'Turned off, no buffet booking is taken on any screen — guests can still book an ordinary table.'
                : isOnline
                  ? 'Applies the moment you confirm — the public page shows “reservations closed”. Existing bookings are unaffected.'
                  : 'Turned off, the till refuses new phone and walk-in bookings. Existing bookings are unaffected.'}
            />
          </Section>

          {/* The money taken at booking, and whether it goes back — one card,
              because they are one decision followed by its consequence. A
              refund rule has nothing to act on until an amount is being taken,
              which is why the second half only appears once the first is on;
              as two cards that read as the page rearranging itself. */}
          <Section
            icon={CreditCard}
            title="Booking amount & refunds"
            right={(
              <button
                type="button"
                onClick={() => setShowRoadmap((v) => !v)}
                aria-expanded={showRoadmap}
                aria-controls="booking-money-roadmap"
                title="What this card cannot do yet"
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${
                  showRoadmap
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
                }`}
              >
                <Info size={15} />
                <span className="sr-only">What this card cannot do yet</span>
              </button>
            )}
          >
            <div className="space-y-4">
              {/* Named as not-yet-built on purpose. A settings page that lists
                  a capability reads as offering it, so each line says plainly
                  that it is coming rather than hiding somewhere on the page. */}
              {showRoadmap && (
                <div
                  id="booking-money-roadmap"
                  className="rounded-xl border border-primary/25 bg-primary/5 p-4 text-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold text-foreground">Not built yet — planned for a later release</p>
                    <button
                      type="button"
                      onClick={() => setShowRoadmap(false)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      aria-label="Close"
                    >
                      <X size={15} />
                    </button>
                  </div>
                  <ul className="mt-2.5 space-y-2 text-muted-foreground list-disc pl-5">
                    <li>
                      <strong className="text-foreground">Refund accounting</strong> — where a refunded
                      booking amount lands in the books, so refunds reconcile instead of being tracked by hand.
                    </li>
                    <li>
                      <strong className="text-foreground">Payment link for phone bookings</strong> — take the
                      booking amount on a reservation made over the phone by sending the guest a payment URL,
                      rather than the amount only being collectable online.
                    </li>
                  </ul>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Today this card sets <em>whether</em> an amount is taken, <em>how much</em>,{' '}
                    <em>whether and when</em> it is refunded, and <em>how</em> the refund reaches the
                    guest. Everything above is still manual.
                  </p>
                </div>
              )}
              <Toggle
                checked={val('payment_required')}
                onChange={(v) => setV('payment_required', v)}
                label={isBuffet ? 'Take payment when a guest books the buffet'
                  : isOnline ? 'Take a booking amount when reserving on the website'
                  : 'Take a booking amount for counter bookings'}
              />
              {val('payment_required') && (
                <>
                  {/* A buffet is the one booking whose whole price is known
                      before anybody arrives — one ticket per head, no dishes to
                      choose — so it can be paid for outright rather than merely
                      held with a deposit. Nothing else on this page can, which
                      is why the choice appears only on this tab. */}
                  {isBuffet && (
                    <div className="pl-14 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        {
                          mode: 'FIXED', icon: CreditCard,
                          title: 'A fixed booking amount',
                          blurb: 'A holding amount you set below, deducted from the bill when the party sits down.',
                        },
                        {
                          mode: 'FULL', icon: Wallet,
                          title: 'The full buffet price',
                          blurb: 'The whole per-head ticket for the party — adults and children at that day’s prices. Nothing left to pay at the table.',
                        },
                      ].map(({ mode, icon: Icon, title, blurb }) => {
                        const on = (s.buffet_amount_mode || 'FIXED') === mode;
                        return (
                          <button
                            key={mode}
                            type="button"
                            onClick={() => set('buffet_amount_mode', mode)}
                            className={`text-left rounded-lg border p-4 transition ${
                              on
                                ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                : 'border-border bg-card hover:border-muted-foreground/40'
                            }`}
                          >
                            <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                              <Icon size={15} className={on ? 'text-primary' : 'text-muted-foreground'} />
                              {title}
                            </span>
                            <span className="mt-1 block text-xs text-muted-foreground">{blurb}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {/* Charging the full price leaves nothing to type: the number
                      is the buffet's own, per party, per day. */}
                  {isBuffet && s.buffet_amount_mode === 'FULL' ? (
                    <p className="pl-14 text-xs text-muted-foreground">
                      No amount to set — each booking is charged its own party’s ticket,
                      priced from the buffet’s adult and child prices for the day being booked.
                    </p>
                  ) : (
                    <div className="pl-14">
                      <NumberField
                        label="Booking amount"
                        unit="Rs"
                        placeholder="100"
                        value={val('booking_amount') || ''}
                        onChange={(e) => setV('booking_amount', e.target.value)}
                        hint="Charged once per booking; deducted from the bill when the guest sits down."
                      />
                    </div>
                  )}

                  {/* Under a hairline: still its own decision — whether the
                      money comes back — just no longer its own card. */}
                  <div className="pt-4 border-t border-border space-y-4">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      <Undo2 size={13} /> Refunds
                    </div>
                    <Toggle
                      checked={val('refund_allowed')}
                      onChange={(v) => setV('refund_allowed', v)}
                      label="Refund the booking amount on cancellation"
                    />
                    {val('refund_allowed') && (
                      <div className="pl-14 space-y-4">
                        <NumberField
                          label="Full refund when cancelled at least this long before"
                          unit="min"
                          value={val('refund_cutoff_minutes')}
                          onChange={(e) => setV('refund_cutoff_minutes', e.target.value)}
                          hint="Cancelled later than this, the booking amount is forfeited."
                        />
                        {/* WHEN the money goes back was already answered above;
                            this is HOW it arrives — the half of the promise the
                            guest actually experiences. */}
                        <MethodPicker
                          label="How the refund reaches the guest"
                          value={val('refund_method')}
                          onChange={(v) => setV('refund_method', v)}
                          options={REFUND_METHODS}
                          hint="Told to the guest when they book and again when they cancel, and shown on the booking for whoever pays it out. No payment gateway is connected yet, so the payout itself is still made by hand."
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Section>

          {/* Cancellation rules — separate from refunds above: this decides
              whether the guest may call the booking off at all, and how late. */}
          <Section icon={CalendarX} title="Cancellation rules">
            <div className="space-y-4">
              <Toggle
                checked={val('cancellation_allowed')}
                onChange={(v) => setV('cancellation_allowed', v)}
                label={isOnline ? 'Let guests cancel their booking on the website' : 'Let guests cancel a counter booking themselves'}
              />
              {val('cancellation_allowed') && (
                <div className="pl-14 space-y-3">
                  <NumberField
                    label="Cancellation allowed until this long before the booking"
                    unit="min"
                    value={val('cancel_cutoff_minutes')}
                    onChange={(e) => setV('cancel_cutoff_minutes', e.target.value)}
                  />
                  {/* A guest calling a booking off asks both questions at once
                      — "can I?" and "do I get my money back?" — so the answer
                      set on the money card is repeated here rather than left to
                      be hunted for. Read-only: it is edited in one place. */}
                  {val('payment_required') && Number(val('booking_amount')) > 0 && (
                    <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      {val('refund_allowed')
                        ? `Cancelled at least ${val('refund_cutoff_minutes')} min before, the booking amount goes back — ${refundMethodLabel(val('refund_method')).toLowerCase()}.`
                        : 'The booking amount is not refunded on cancellation.'}
                      {' '}Set under <em>Booking amount &amp; refunds</em>.
                    </p>
                  )}
                </div>
              )}
            </div>
          </Section>

          {/* Confirmation */}
          <Section icon={BadgeCheck} title="Booking confirmation">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setV('auto_confirm', true)}
                className={`text-left rounded-lg border p-4 transition ${
                  val('auto_confirm')
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                }`}
              >
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <Zap size={15} className={val('auto_confirm') ? 'text-primary' : 'text-muted-foreground'} />
                  Direct confirmation
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  The table is confirmed the moment the booking is made.
                </span>
              </button>
              <button
                type="button"
                onClick={() => setV('auto_confirm', false)}
                className={`text-left rounded-lg border p-4 transition ${
                  !val('auto_confirm')
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                }`}
              >
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <UserCheck size={15} className={!val('auto_confirm') ? 'text-primary' : 'text-muted-foreground'} />
                  Staff will approve it
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Each booking arrives as a request to approve or decline on All Reservations.
                </span>
              </button>
            </div>
            {/* Said plainly rather than left to be discovered: with money taken
                up front, this choice only governs the minutes before the guest
                pays. Once they have, the booking confirms itself — its covers
                were already checked against the day's capacity, so there is
                nothing for anybody to approve. */}
            {isBuffet && val('payment_required') && (
              <p className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                A buffet booking that is <strong className="text-foreground">paid</strong> confirms itself —
                it never waits in the approval queue. This setting decides only what happens to a
                booking whose payment window ran out before the guest paid.
              </p>
            )}
          </Section>

          {/* Phone verification — offline only. The online booking page already
              signs the guest in with an OTP before they can reserve, so asking
              again there would be a second code for the same phone. What has
              never been checked is a number staff typed in: a booking phoned in
              under a made-up number holds a real table all evening. */}
          {!isOnline && (
            <Section icon={ShieldCheck} title="Phone verification (OTP)">
              <div className="space-y-4">
                <Toggle
                  checked={s.phone_booking_otp_required}
                  onChange={(v) => set('phone_booking_otp_required', v)}
                  label="Verify the number on phone reservations"
                />
                <Toggle
                  checked={s.walkin_booking_otp_required}
                  onChange={(v) => set('walkin_booking_otp_required', v)}
                  label="Verify the number on walk-in reservations"
                />
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  No SMS gateway is connected yet — the code is shown on the till screen for
                  staff to read out.
                </p>
              </div>
            </Section>
          )}

          </div>

          {/* Right column — bookable areas plus the smaller service rules, so
              the two columns land at roughly the same height instead of the
              left running long over an empty right. */}
          <div className="space-y-4">
          <Section icon={LayoutGrid}
            title={isBuffet ? 'Tables for buffet parties' : 'Bookable areas & tables'}
            hint={isBuffet
              ? 'Tick the tables a buffet party may be seated at — the ones near the counters. A buffet booking is then offered ONLY these, on the website and the till alike. Tick none and it falls back to the tables that channel already allows.'
              : undefined}
            right={allTableIds.length > 0 && (
              <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                {selectedIds.size === 0 ? allTableIds.length : selectedIds.size} of {allTableIds.length} open
              </span>
            )}>
            {tablesTree.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No tables set up yet. Add floors, sections and tables under Tables first.
              </p>
            ) : (
              <div className="space-y-4">
                {tablesTree.map((f) => (
                  <div key={f.floor_id}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      {f.location_name ? `${f.location_name} · ` : ''}{f.floor_name}
                    </div>
                    <div className="space-y-3">
                      {f.sections.map((sec) => {
                        const ids = sec.tables.map((t) => t.id);
                        const allOn = ids.every(isTableOn);
                        const someOn = ids.some(isTableOn);
                        return (
                          <div key={sec.section_id} className="rounded-lg border border-border p-3">
                            <label className="flex items-center gap-2 cursor-pointer select-none mb-2">
                              <input
                                type="checkbox"
                                checked={allOn}
                                ref={(el) => { if (el) el.indeterminate = !allOn && someOn; }}
                                onChange={() => toggleSection(sec)}
                                className="h-4 w-4 accent-primary"
                              />
                              <span className="text-sm font-medium text-foreground">{sec.section_name}</span>
                              <span className="text-xs text-muted-foreground">({sec.tables.length})</span>
                            </label>
                            <div className="flex flex-wrap gap-1.5 pl-6">
                              {sec.tables.map((t) => {
                                const on = isTableOn(t.id);
                                return (
                                  <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => toggleTable(t.id)}
                                    className={`rounded-md border px-2 py-1 text-xs font-medium transition ${
                                      on
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-border bg-muted text-muted-foreground line-through'
                                    }`}
                                    title={on ? 'Bookable — click to close' : 'Not bookable — click to open'}
                                  >
                                    {t.table_code} · {t.capacity}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Table hold WHILE THE GUEST PAYS — the website channel's twin of
              the buffet seat hold: with a booking amount on, the table is
              taken the moment the booking is made and kept for these minutes.
              Pay inside them and the booking confirms itself; let them run
              out and the table is freed and the lapse counted on the guest.
              A different clock from "Table holding" below, which starts when
              the guest is LATE. */}
          {tab === 'online' && (
            <Section icon={Hourglass} title={t('settings.hold.payTitle')}
              hint={t('settings.hold.payHint')}>
              <div className="space-y-4">
                <NumberField
                  label={t('settings.hold.minutes')}
                  unit="min"
                  value={s.table_hold_minutes ?? 5}
                  onChange={(e) => set('table_hold_minutes', e.target.value)}
                  hint={t('settings.hold.minutesHint')}
                />
                <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('settings.hold.how')}
                  </p>
                  <div className="flex items-start gap-2.5 text-sm">
                    <Hourglass size={15} className="mt-0.5 shrink-0 text-slate-600" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">{t('settings.hold.factHeldT')}</strong>
                      {' — '}{t('settings.hold.factHeld')}
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 text-sm">
                    <Zap size={15} className="mt-0.5 shrink-0 text-primary" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">{t('settings.hold.factPayT')}</strong>
                      {' — '}{t('settings.hold.factPay')}
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 text-sm">
                    <CalendarX size={15} className="mt-0.5 shrink-0 text-red-500" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">{t('settings.hold.factFreeT')}</strong>
                      {' — '}{t('settings.hold.factFree')}
                    </p>
                  </div>
                </div>
              </div>
            </Section>
          )}
          {/* Table holding — the number, then what actually happens when it
              runs out, as three short facts rather than a wall of grey text.
              The copy matters: the hold auto-cancels EXCEPT paid bookings,
              and a no-show is never automatic. */}
          <Section icon={Timer} title="Table holding"
            hint="How long a booked table stays reserved past its booked time.">
            <div className="space-y-4">
              <NumberField
                label="Hold the table for"
                unit="min"
                value={val('hold_table_minutes')}
                onChange={(e) => setV('hold_table_minutes', e.target.value)}
                hint="Counted from the booked time. Staff can grant single bookings extra minutes on top."
              />
              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  When the hold runs out
                </p>
                <div className="flex items-start gap-2.5 text-sm">
                  <CalendarX size={15} className="mt-0.5 shrink-0 text-red-500" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">The booking cancels itself</strong> and the table
                    goes back on the floor — logged in the booking's history as
                    <em> Auto-cancelled — hold ran out</em>.
                  </p>
                </div>
                <div className="flex items-start gap-2.5 text-sm">
                  <CreditCard size={15} className="mt-0.5 shrink-0 text-amber-600" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Except when a booking amount was paid</strong> —
                    cancelling would decide the guest's money (refund or forfeit), so the booking stays
                    flagged as overdue on All Reservations and the till until staff choose.
                  </p>
                </div>
                <div className="flex items-start gap-2.5 text-sm">
                  <UserX size={15} className="mt-0.5 shrink-0 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">A no-show is always a person's call</strong> — staff
                    mark it (it counts on the guest's profile), or press <em>Extend hold</em> if the guest
                    rang to say they're late. Every decision lands in the booking's history.
                  </p>
                </div>
              </div>
            </div>
          </Section>

          {/* The biggest party any ONE booking may take. Not a channel rule:
              a table for twenty is the same imposition on the room whether it
              was booked on the website, over the phone or at the counter. 0 =
              no cap, which is how every restaurant starts. Staff seating a
              walk-in are not bound by it — nobody is booking anything. */}
          <Section icon={Users} title={t('settings.party.title')}
            hint={t('settings.party.hint')}>
            <NumberField
              label={t('settings.party.max')}
              unit={t('settings.party.unit')}
              value={s.max_party_size ?? 0}
              onChange={(e) => set('max_party_size', e.target.value)}
              hint={t('settings.party.maxHint')}
            />
          </Section>

          {/* The blacklist, and the one thing that feeds it: a guest who books
              and never turns up. It sat on the payment-hold card while it
              counted lapsed holds; a hold that ran out costs the restaurant
              minutes, a no-show costs it the whole sitting, so the rule is
              made of no-shows and lives on its own. */}
          <Section icon={UserX} title={t('settings.noshow.title')}
            hint={t('settings.noshow.hint')}>
            <div className="space-y-4">
              <NumberField
                label={t('settings.noshow.strikes')}
                unit={t('settings.noshow.strikesUnit')}
                value={s.no_show_strikes_limit ?? 3}
                onChange={(e) => set('no_show_strikes_limit', e.target.value)}
                hint={t('settings.noshow.strikesHint')}
              />
              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-start gap-2.5 text-sm">
                  <UserX size={15} className="mt-0.5 shrink-0 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('settings.noshow.factMarkT')}</strong>
                    {' — '}{t('settings.noshow.factMark')}
                  </p>
                </div>
                <div className="flex items-start gap-2.5 text-sm">
                  <UserCheck size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('settings.noshow.factLiftT')}</strong>
                    {' — '}{t('settings.noshow.factLift')}
                  </p>
                </div>
              </div>
            </div>
          </Section>

          {/* Seat hold — buffet only, and a different clock from the table
              hold above. That one starts when the guest is LATE; this one
              starts the moment they book, because a buffet booking claims
              covers out of a fixed number the instant it is written and an
              unpaid claim must not sit on them while the counter fills up.
              Shown whether or not payment is on: with payment off nothing is
              ever owed, so no booking is ever put on the clock, and the number
              is simply waiting for the day the restaurant turns payment on. */}
          {isBuffet && (
            <Section icon={Hourglass} title="Seat hold while the guest pays"
              hint="A buffet booking takes its seats the moment it is made. This is how long it keeps them without paying.">
              <div className="space-y-4">
                <NumberField
                  label="Hold the seats for"
                  unit="min"
                  value={s.buffet_seat_hold_minutes}
                  onChange={(e) => set('buffet_seat_hold_minutes', e.target.value)}
                  hint="Counted from the moment the booking is made. 0 turns the clock off — an unpaid booking then keeps its seats until somebody cancels it."
                />
                <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    How a buffet booking runs
                  </p>
                  <div className="flex items-start gap-2.5 text-sm">
                    <Hourglass size={15} className="mt-0.5 shrink-0 text-amber-600" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">The seats are taken straight away</strong> — the
                      guest sees a countdown while they pay, and the covers are already off the
                      day’s capacity so nobody else can be sold them.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 text-sm">
                    <Zap size={15} className="mt-0.5 shrink-0 text-primary" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">Paying confirms it — you approve nothing.</strong>{' '}
                      The seats were already checked against the day’s capacity, so there is nothing
                      left to decide; the booking lands on your screens confirmed.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 text-sm">
                    <CalendarX size={15} className="mt-0.5 shrink-0 text-red-500" />
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">The clock running out cancels it</strong> and puts
                      the covers straight back — logged in the booking’s history as
                      <em> Auto-cancelled — payment window ran out</em>.
                    </p>
                  </div>
                </div>
              </div>
            </Section>
          )}

          {/* Pre-ordering — not on the buffet tab. A buffet is one price per
              head and one counter everybody helps themselves from: there are
              no dishes to choose in advance, so the question has no answer,
              and a kitchen order fired at check-in would be food nobody
              ordered on top of a buffet already paid for. The server refuses
              it for a buffet booking too, so this is not the only guard. */}
          {!isBuffet && (
            <Section icon={UtensilsCrossed} title="Pre-ordering">
              <Toggle
                checked={val('allow_preorder')}
                onChange={(v) => setV('allow_preorder', v)}
                label="Let guests pre-order their meal with the booking"
                hint="The meal is saved with the booking and fires to the kitchen the moment the guest checks in."
              />
            </Section>
          )}
          </div>
          </div>

        </div>
      )}
    </div>
  );
}
